import _ from 'lodash';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { SortOrderUp } from 'enum/SortOrder';
import SERVICE_TYPE_ID from 'constants/ServiceTypeId';
import { useTypedDispatch } from 'hooks/useTypedDispatch';
import { IServiceWithAccountRequest } from 'interfaces/IUser';
import AppMultiSelectSearch, { ISearchRequest } from 'components/shared/AppSelect/AppMultiSelectSearch';
import {
  servicesSelector,
  totalCountSelector,
  isServicesLoadingSelector,
} from 'store/services/servicesSelectors';
import { IVaultServiceWithAccount, resetServicesState } from 'store/services/servicesReducer';
import { getServices } from 'store/services/servicesThunk';
import { getServicesApi } from 'services/api/servicesApi';

interface IFormData { allowedDbIds: IVaultServiceWithAccount[] }
export enum FormItemName { ALLOWED_DB_IDS = 'allowedDbIds' }

const AllowedDbIdsSelector: FC<{
  allowedDbIds: string[],
  onChange: (ids: { accountId: number, serviceId: number }[]) => void,
  isValid: (valid: boolean) => void,
}> = ({
  allowedDbIds, onChange, isValid,
}) => {
  const { t } = useTranslation('plugIn');

  const dispatch = useTypedDispatch();
  const [onInit, setOnInit] = useState(true);
  const services = useSelector(servicesSelector);
  const totalCount = useSelector(totalCountSelector);
  const isLoading = useSelector(isServicesLoadingSelector);

  useEffect(() => () => { dispatch(resetServicesState()); }, []);

  const schema = useMemo(
    () => yup.object().shape({
      [FormItemName.ALLOWED_DB_IDS]: yup.array().required(),
    }),
    [],
  );
  const methods = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: { allowedDbIds: [] },
  });
  const { control, watch, setValue } = methods;
  const selectedObjects = watch(FormItemName.ALLOWED_DB_IDS);

  useEffect(() => {
    if (allowedDbIds.length) {
      getServicesApi({
        serviceIdFiltering: { in: allowedDbIds.map((dbIds) => +dbIds.split('_').at(-2)!) },
        pageSize: allowedDbIds.length,
      }).then((res) => {
        setValue(FormItemName.ALLOWED_DB_IDS, res.content);
        setOnInit(false);
      });
    } else {
      setOnInit(false);
    }
  }, [allowedDbIds]);

  const handleGetObjects = useCallback((data: ISearchRequest) => {
    const { searchValue, ...pagination } = data;
    const payload: IServiceWithAccountRequest = {
      ...pagination,
      serviceTypeIdFiltering: { eq: String(SERVICE_TYPE_ID) },
      accountIdSorting: SortOrderUp.ASC,
    };
    if (searchValue) {
      payload.nameFiltering = {
        contains: searchValue,
      };
    }
    dispatch(getServices(payload, true));
  }, []);

  const onChangeValue = useCallback((arr: IVaultServiceWithAccount[]) => {
    const ids = arr.map((s) => `account_${s.accountId}_service_${s.serviceId}_db`);
    isValid(!_.isEqual(_.sortBy(ids), _.sortBy(allowedDbIds)));
    onChange(arr);
  }, [onChange, isValid, allowedDbIds]);

  return (
    <FormProvider {...methods} control={control}>
      <div className="break-flex" />
      <AppMultiSelectSearch
        async
        name={t('allowedDbIds')}
        formItemName={FormItemName.ALLOWED_DB_IDS}
        options={onInit ? [] : services}
        totalCount={totalCount ?? 0}
        isLoading={onInit || isLoading}
        isOptionEqualToValue={(a, b) => a.serviceId === b.serviceId}
        getOptionValue={(p) => `account_${p.accountId}_service_${p.serviceId}_db`}
        getOptionLabel={(p) => p.name}
        onChangeValue={onChangeValue}
        getOptions={handleGetObjects}
        className="allowed-db-ids-select"
        dataTestId="allowed-db-ids-select"
        fieldStyle={{ fontSize: '17px' }}
        selectedOption={selectedObjects}
      />
    </FormProvider>
  );
};

export default AllowedDbIdsSelector;
